import { useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation, useParams } from "react-router";
import type { AccessPermission } from "@entrilia/shared/api/types";
import { useClientPermissionsContext } from "@entrilia/shared/contexts/ClientPermissionsContext";
import { ClientPermissionsContextProvider } from "@entrilia/shared/contexts/ClientPermissionsContextProvider";
import biApi from "../../../../../api/biApi";
import useClientContext from "../../../../../context/useClientContext";
import { GroupsContextProvider } from "../../report-lists/groups/contexts/GroupsContextProvider";
import useReportGroups from "../../sv/components/builder/common/hooks/useReportGroups";
import ImportantDataLoadingFailed from "../../sv/components/common/ImportantDataLoadingFailed";
import type { BiApiClient } from "../../sv/contexts/ApiClientProviderContext";
import { BiApiClientProvider } from "../../sv/contexts/BiApiClientProvider";
import { useAppDispatch } from "../../sv/store/hooks/useAppDispatch";
import { createCurrentReportAction, createDrillDownPageAction } from "../../sv/store/utils/storeUtils";
import { bindArtifactReportApi, createArtifactReportApiClient } from "./artifactReportApiClient";
import { toDashboardPath } from "./dashboardPath";
import { isSourceReportRequest } from "./sourceReportRequest";

const withoutCustomMeasureManagement = (permissions: AccessPermission[]): AccessPermission[] =>
  permissions.filter((permission) => permission !== "ManageCustomMeasures");

const ArtifactReportContainer = () => {
  const { id } = useParams();
  const { state, pathname } = useLocation();
  const { clientCode } = useClientContext();
  const { permissions, permissionSets } = useClientPermissionsContext();
  const dispatch = useAppDispatch();
  const { reportGroups, actions, ui } = useReportGroups({ apiClient: biApi.uiReports });

  const apiClient = useMemo(
    () =>
      id && isSourceReportRequest(state) ? createArtifactReportApiClient(bindArtifactReportApi(id, state)) : undefined,
    [id, state]
  );

  useEffect(
    () => () => {
      dispatch(createDrillDownPageAction("removeAll"));
      dispatch(createCurrentReportAction("reset"));
    },
    [dispatch]
  );

  if (!apiClient) {
    return <Navigate to={toDashboardPath(pathname)} replace />;
  }

  if (ui.error) {
    return (
      <ImportantDataLoadingFailed
        title="Failed to load the report"
        disclaimer="There appears to have been an issue with loading the data. Kindly attempt to reload the report. Should the issue persist, please get in touch with our support team for further assistance."
      />
    );
  }

  return (
    <BiApiClientProvider<BiApiClient> api={apiClient}>
      <GroupsContextProvider groups={{ [clientCode]: reportGroups }} ui={ui} actions={actions}>
        <ClientPermissionsContextProvider
          permissions={withoutCustomMeasureManagement(permissions)}
          permissionSets={permissionSets.map((permissionSet) => ({
            ...permissionSet,
            permissions: withoutCustomMeasureManagement(permissionSet.permissions),
          }))}
        >
          <Outlet />
        </ClientPermissionsContextProvider>
      </GroupsContextProvider>
    </BiApiClientProvider>
  );
};

export default ArtifactReportContainer;
